import { useState, useEffect } from 'react'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import MosSmartDialog from '../common/MosSmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { apiPost } from '../../../generic/Api_Functions'
import AlertUI from '../../../generic/AlertUI'
import { Stack, TextField, Box, CircularProgress, Typography, Button } from '@mui/material'
import useMosInfo from '../common/provider/useMosInfo'

// Shown instead of the pending-only dialog while the setting "Cancel only not started parts" is OFF
// (the backend preview says so). The user picks how many pending, OK and NOK parts to cancel; the system
// chooses which ones. When OK/NOK parts are cancelled, a second step asks whether to create replacement
// parts, which are created pending at the job.
const toQuantity = (value) => (value === '' ? 0 : Number(value))

const replacementsNeeded = (okQuantity, nokQuantity, nokWithAutomaticReplacement) =>
  okQuantity + Math.max(0, nokQuantity - nokWithAutomaticReplacement)

const CancelPartsByQualityDialog = ({ open, onCancel, onSuccess, fabricationOrder, jobReference, preview }) => {
  const { t_ } = useSmartTranslation()
  const { device, worker } = useMosInfo()
  const [AlertElement, showAlert, dismiss] = AlertUI()
  const [pendingQuantity, setPendingQuantity] = useState('')
  const [okQuantity, setOkQuantity] = useState('')
  const [nokQuantity, setNokQuantity] = useState('')
  const [reason, setReason] = useState('')
  const [askReplacements, setAskReplacements] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setPendingQuantity('')
      setOkQuantity('')
      setNokQuantity('')
      setReason('')
      setAskReplacements(false)
      setLoading(false)
      dismiss()
    }
  }, [open, dismiss])

  const pending = toQuantity(pendingQuantity)
  const ok = toQuantity(okQuantity)
  const nok = toQuantity(nokQuantity)
  const replacements = replacementsNeeded(ok, nok, preview.nok.withAutomaticReplacement)

  const submit = async (createReplacements) => {
    setLoading(true)
    try {
      await apiPost(
        'mos/parts/cancel-by-quality/' + worker.id + '/' + device.id + '/' + fabricationOrder.id + '/' + jobReference.id,
        { pendingQuantity: pending, okQuantity: ok, nokQuantity: nok, reason, createReplacements }
      )
      if (onSuccess) onSuccess()
      onCancel()
    } catch (error) {
      setAskReplacements(false)
      showAlert({ severity: 'error', title: t_(error.message) })
    } finally {
      setLoading(false)
    }
  }

  const accept = () => {
    if (![pending, ok, nok].every((quantity) => Number.isInteger(quantity) && quantity >= 0) || pending + ok + nok <= 0) {
      showAlert({ severity: 'warning', title: t_('Quantity must be greater than 0') })
      return
    }
    if (pending > preview.pending) {
      showAlert({ severity: 'warning', title: t_('Cannot cancel more parts than pending in the active job') })
      return
    }
    if (ok > preview.ok.cancellable) {
      showAlert({ severity: 'warning', title: t_('Cannot cancel more OK parts than the ones not yet reported in a later job') })
      return
    }
    if (nok > preview.nok.cancellable) {
      showAlert({ severity: 'warning', title: t_('Cannot cancel more NOK parts than the ones not yet reported in a later job') })
      return
    }
    dismiss()
    if (replacements > 0) {
      setAskReplacements(true)
      return
    }
    submit(false)
  }

  if (askReplacements) {
    return (
      <MosSmartDialog
        title={createDialogTitle({ color: 'primary', title: t_('Create replacement parts') })}
        setOpen={open}
        acceptLabel='YES'
        cancelLabel='NO'
        acceptCallback={() => submit(true)}
        cancelCallback={() => submit(false)}
        autoClose={false}
        disableButtons={loading}
        renderCustomButtons={
          <Button variant='outlined' disabled={loading} onClick={() => setAskReplacements(false)}>
            {t_('BACK')}
          </Button>
        }
      >
        <Stack spacing={2} sx={{ mt: 1, p: 2 }}>
          <Typography>{t_('Do you want to create {{count}} replacement parts as pending?', { count: replacements })}</Typography>
          {nok > 0 && preview.nok.withAutomaticReplacement > 0 && (
            <Typography variant='body2' color='text.secondary'>
              {t_('NOK parts that already have an automatic replacement do not get another one')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, visibility: loading ? 'visible' : 'hidden' }}>
            <CircularProgress size={24} />
          </Box>
        </Stack>
      </MosSmartDialog>
    )
  }

  const someReportedPartCannotBeCancelled =
    preview.ok.cancellable < preview.ok.total || preview.nok.cancellable < preview.nok.total

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Cancel parts') })}
      setOpen={open}
      cancelCallback={onCancel}
      acceptCallback={accept}
      disableButtons={loading}
    >
      {AlertElement}
      <Stack spacing={2} sx={{ mt: 1, p: 2 }}>
        <TextField
          fullWidth
          type='number'
          value={pendingQuantity}
          label={t_('Pending parts to cancel')}
          onChange={(e) => setPendingQuantity(e.target.value)}
          size='small'
          sx={{ mt: 1 }}
          inputProps={{ min: 0, max: preview.pending }}
          helperText={t_('Pending: {{pending}}', { pending: preview.pending })}
        />
        <TextField
          fullWidth
          type='number'
          value={okQuantity}
          label={t_('OK parts to cancel')}
          onChange={(e) => setOkQuantity(e.target.value)}
          size='small'
          inputProps={{ min: 0, max: preview.ok.cancellable }}
          helperText={t_('Reported: {{total}} · cancellable: {{cancellable}}', { total: preview.ok.total, cancellable: preview.ok.cancellable })}
        />
        <TextField
          fullWidth
          type='number'
          value={nokQuantity}
          label={t_('NOK parts to cancel')}
          onChange={(e) => setNokQuantity(e.target.value)}
          size='small'
          inputProps={{ min: 0, max: preview.nok.cancellable }}
          helperText={t_('Reported: {{total}} · cancellable: {{cancellable}}', { total: preview.nok.total, cancellable: preview.nok.cancellable })}
        />
        {someReportedPartCannotBeCancelled && (
          <Typography variant='body2' color='text.secondary'>
            {t_('Parts already reported in a later job cannot be cancelled')}
          </Typography>
        )}
        <TextField
          fullWidth
          multiline
          rows={3}
          value={reason}
          label={t_('Cancellation reason (optional)')}
          onChange={(e) => setReason(e.target.value)}
          size='small'
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, visibility: loading ? 'visible' : 'hidden' }}>
          <CircularProgress size={24} />
        </Box>
      </Stack>
    </MosSmartDialog>
  )
}

export default CancelPartsByQualityDialog
